import type { ListKey, ListState, RunDraft, RunView, StreamItem } from '../shared/types.js';
import { autosave } from './autosave.js';
import { boot, h, quoted, t, tl, tobj } from './dom.js';
import { creep, milestone, pageObservations, readingStep } from './progress.js';
import { pageLabel, renderPagesList, streamItemNode } from './render-shared.js';

export interface LesenActions {
  /** «Weiter»: the stage leaves the screen for the wait step; main.ts brings it back when the request fails. */
  proceed(description: string | null): void;
  draft(description: NonNullable<RunDraft['description']>): Promise<unknown>;
}

export interface LesenStage {
  el: HTMLElement;
  onStatus(list: Partial<Record<ListKey, ListState>>): void;
  onItem(item: StreamItem): void;
  onDone(): void;
  onError(message: string): void;
  description(): string;
  /** The description box as it stands, so the stage can be rendered again with it. */
  draft(): NonNullable<RunDraft['description']>;
}

/** A popup over the one-screen layout: bottom sheet on a phone, centred dialog on wider screens. */
function sheet(id: string, title: string, ...children: Node[]): { el: HTMLDialogElement; body: HTMLElement } {
  const close = h('button', { class: 'link', type: 'button' }, t('lesen.popup_close'));
  const body = h('div', { class: 'sheet-body' }, children);
  const el = h('dialog', { class: 'sheet', id, 'aria-labelledby': `${id}-title` }, h('div', { class: 'sheet-head' }, h('h2', { id: `${id}-title` }, title), close), body);
  close.addEventListener('click', () => el.close());
  // A click on the backdrop lands on the dialog element itself.
  el.addEventListener('click', (event) => {
    if (event.target === el) el.close();
  });
  return { el, body };
}

/** What the stage gives up when the screen is too short, in this order (app.css): the closing line, the third
 * textarea row, the generous spacing, the note of the description card. */
const FIT_LEVELS = ['fit-1', 'fit-2', 'fit-3', 'fit-4'];

/** Keeps the stage on one screen: measures, and compacts only as far as needed. Any string or domain may be long,
 * so fixed breakpoints would not hold. Returns the fit to call whenever the content changes its height. */
function keepOnOneScreen(root: HTMLElement, textarea: HTMLTextAreaElement): () => void {
  const fit = () => {
    if (!root.isConnected) return;
    root.classList.remove(...FIT_LEVELS);
    for (const level of FIT_LEVELS) {
      if (document.documentElement.scrollHeight <= window.innerHeight) break;
      root.classList.add(level);
    }
  };
  let width = window.innerWidth;
  const onResize = () => {
    if (!root.isConnected) return window.removeEventListener('resize', onResize);
    // An on-screen keyboard that shrinks the window must not reshuffle the card under the typing visitor.
    const keyboard = document.activeElement === textarea && window.innerWidth === width;
    width = window.innerWidth;
    if (!keyboard) fit();
  };
  window.addEventListener('resize', onResize);
  // First fit once the stage is on the page, before it is painted.
  requestAnimationFrame(fit);
  return fit;
}

/** `notice`: why the visitor is back here after «Weiter» (the Rückfragen could not be prepared). */
export function renderLesen(view: RunView, actions: LesenActions, notice: string | null = null): LesenStage {
  const root = h('section', { class: 'stage on lesen' });
  root.append(h('h1', {}, `${t('lesen.h1')} `, h('b', { id: 'dom' }, view.domain)));

  // Progress: the step in work, the bar and the observation count. The list and the stream live in a popup,
  // so the whole stage fits one screen.
  const states = tobj<Record<string, string>>('lesen.page_states');
  const stepLabel = h('b', { id: 'pstep' });
  const percent = h('span', { class: 'pct', id: 'ppct', 'aria-hidden': 'true' });
  const fill = h('i');
  const bar = h('div', { class: 'bar', id: 'pbar', role: 'progressbar', 'aria-label': t('lesen.progress.aria'), 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': 0 }, fill);
  const total = h('b', { id: 'pcnt' }, String(view.stream.length));
  const detailsButton = h('button', { class: 'link', type: 'button', id: 'details-open', 'aria-haspopup': 'dialog' }, t('lesen.details.open'));
  root.append(
    h(
      'div',
      { class: 'reading', id: 'reading' },
      h('div', { class: 'reading-head' }, stepLabel, percent),
      bar,
      h('div', { class: 'reading-meta' }, h('span', { 'aria-live': 'off' }, h('span', { class: 'pulse' }), total, ` ${t('lesen.counter_label')}`), detailsButton)
    )
  );

  // Optional description card: written before any observation is on screen, hints only on request.
  const desc = boot.config.description;
  // The draft on the server: what was saved with «Übernehmen», or what was typed so far.
  const draft = view.draft?.description ?? null;
  let saved = view.description ?? (draft?.saved ? draft.text : '');
  let editing = !saved;
  // The badge above the title says the card may stay empty; a blank string in strings.de.json drops it.
  const optional = t('lesen.description.optional');
  const textarea = h('textarea', { id: 'desc', rows: 3, maxlength: desc.max_chars, 'aria-label': t('lesen.description.aria'), 'aria-describedby': optional ? 'desc-optional desc-note' : 'desc-note', placeholder: t('lesen.description.placeholder') }) as HTMLTextAreaElement;
  textarea.value = saved || draft?.text || '';
  // The box survives a reload and travels with the link.
  const box = () => ({ text: editing ? textarea.value : saved, saved: !editing });
  const save = autosave(root, box, actions.draft);
  const fit = keepOnOneScreen(root, textarea);
  const counter = h('span', { class: 'small muted', id: 'desc-count' });
  const saveButton = h('button', { class: 'btn secondary', id: 'desc-save', type: 'button', disabled: true }, t('lesen.description.save'));
  // The help popup: the prompting questions and an example to read, never pasted into the visitor's box.
  const help: Node[] = [];
  if (desc.hints) help.push(h('p', {}, t('lesen.description.hints_intro')), h('ul', { class: 'hints' }, tl('lesen.description.hints').map((hint) => h('li', {}, hint))));
  if (desc.example) help.push(h('p', { class: 'small muted eg' }, t('lesen.description.example_label')), h('div', { class: 'q', id: 'desc-example' }, quoted(t('lesen.description.example'))));
  const hints = sheet('hints', t('lesen.description.hints_show'), ...help);
  const hintToggle = h('button', { class: 'link', type: 'button', id: 'hint-toggle', hidden: help.length === 0, 'aria-haspopup': 'dialog' }, t('lesen.description.hints_show'));
  hintToggle.addEventListener('click', () => hints.el.showModal());
  const update = () => {
    const length = textarea.value.length;
    counter.textContent = t('lesen.description.counter', { n: length, max: desc.max_chars });
    // No count on an empty box.
    counter.hidden = length === 0;
    saveButton.disabled = textarea.value.trim().length < desc.min_chars;
  };
  textarea.addEventListener('input', () => {
    update();
    save();
  });
  update();
  const card = h(
    'div',
    { class: 'card desc', id: 'desc-card', hidden: !desc.enabled || !editing },
    optional ? h('span', { class: 'badge', id: 'desc-optional' }, optional) : null,
    h('h3', {}, t('lesen.description.title')),
    h('p', { class: 'small muted note', id: 'desc-note' }, t('lesen.description.note')),
    textarea,
    h('div', { class: 'desc-tools' }, hintToggle),
    h('div', { class: 'actions' }, saveButton, counter)
  );
  const echo = h('p', { id: 'desc-echo' }, saved ? `«${saved}»` : '');
  const editButton = h('button', { class: 'link', type: 'button', id: 'desc-edit' }, t('lesen.description.edit'));
  const done = h('div', { class: 'card desc-done', id: 'desc-done', hidden: !desc.enabled || editing }, h('span', { class: 'small muted' }, t('lesen.description.saved_label')), echo, editButton);
  saveButton.addEventListener('click', () => {
    saved = textarea.value.trim();
    editing = false;
    echo.textContent = `«${saved}»`;
    card.hidden = true;
    done.hidden = false;
    fit();
    save.now();
  });
  editButton.addEventListener('click', () => {
    editing = true;
    done.hidden = true;
    card.hidden = false;
    fit();
    textarea.focus();
    save.now();
  });
  root.append(card, done, hints.el);

  // The popup with what the bar summarises: the reading list and every observation as it arrives.
  let pages = renderPagesList(view.list, view.pages);
  const count = h('b', { id: 'cnt' }, String(view.stream.length));
  const countLabel = h('span', { id: 'cntlabel' }, t('lesen.counter_busy'));
  const stream = h('ul', { class: 'stream', id: 'stream' });
  for (const item of view.stream) stream.append(streamItemNode(item, true));
  const details = sheet(
    'details',
    t('lesen.details.title'),
    h('p', { class: 'small muted' }, t('lesen.intro')),
    pages,
    h('div', { class: 'counter' }, h('span', {}, h('span', { class: 'pulse' }), count, ` ${t('lesen.counter_label')}`), countLabel),
    stream
  );
  detailsButton.addEventListener('click', () => details.el.showModal());
  root.append(details.el);

  const errorBox = h('p', { class: 'error', hidden: !notice }, notice ?? '');
  root.append(errorBox);
  const next = h('button', { class: 'btn', id: 'toq', type: 'button', disabled: view.status === 'lesen' }, t('lesen.next'));
  next.addEventListener('click', () => {
    next.disabled = true;
    // Text still in the box when the visitor proceeds counts as saved. The wait is a step of its own
    // (stage-vorbereiten.ts), so nothing on this screen turns into a busy state.
    const typed = textarea.value.trim();
    actions.proceed((editing && typed.length >= desc.min_chars ? typed : saved) || null);
  });
  root.append(h('div', { class: 'actions next' }, next));
  // What comes after the button; a blank string in strings.de.json drops the line.
  const later = t('lesen.later');
  const laterLine = h('p', { class: 'later', id: 'later', hidden: !later }, later);
  root.append(laterLine);

  // Once the site is read and the box is untouched, the run continues on its own after a few seconds: the wait
  // is the step where most visitors leave, and an empty box needs no «Weiter». Typing or opening a popup stops it.
  const autoSeconds = Number(desc.auto_continue_seconds) || 0;
  let countdown = 0;
  const stopAuto = () => {
    if (!countdown) return;
    window.clearInterval(countdown);
    countdown = 0;
    laterLine.textContent = later;
    laterLine.hidden = !later;
    laterLine.classList.remove('auto');
  };
  const startAuto = () => {
    if (countdown || autoSeconds <= 0 || !desc.enabled || !editing || textarea.value.trim() || saved || notice) return;
    let left = autoSeconds;
    laterLine.classList.add('auto');
    laterLine.hidden = false;
    laterLine.textContent = t('lesen.auto_continue', { n: left });
    countdown = window.setInterval(() => {
      if (!root.isConnected) return stopAuto();
      left -= 1;
      if (left <= 0) {
        stopAuto();
        if (!next.disabled) next.click();
        return;
      }
      laterLine.textContent = t('lesen.auto_continue', { n: left });
    }, 1000);
  };
  for (const event of ['input', 'focus', 'pointerdown']) textarea.addEventListener(event, stopAuto);
  hintToggle.addEventListener('click', stopAuto);
  detailsButton.addEventListener('click', stopAuto);

  let n = view.stream.length;
  let observations = pageObservations(view.stream);
  let list = view.list;
  let shown = milestone(list, observations);
  let finished = false;
  let timer = 0;
  const paint = (value: number) => {
    fill.style.width = `${value}%`;
    bar.setAttribute('aria-valuenow', String(value));
    percent.textContent = t('lesen.progress.percent', { n: value });
  };
  const advance = (tick = false) => {
    if (finished) return;
    const target = milestone(list, observations);
    shown = tick ? creep(shown, target) : Math.max(shown, target);
    paint(Math.round(shown));
    const step = readingStep(list, observations);
    const label = step.kind === 'list' ? t('lesen.progress.step', { page: pageLabel(step.key), state: states[step.state] ?? step.state }) : step.kind === 'collecting' ? t('lesen.progress.collecting') : t('lesen.counter_busy');
    if (stepLabel.textContent !== label) {
      stepLabel.textContent = label;
      fit();
    }
  };
  const finish = (state: 'is-done' | 'is-bad', label: string) => {
    finished = true;
    window.clearInterval(timer);
    root.classList.add(state);
    stepLabel.textContent = label;
  };
  advance();
  // The creep between two events; ends with the run or with the stage leaving the page.
  if (view.status === 'lesen') {
    timer = window.setInterval(() => {
      if (root.isConnected) advance(true);
      else window.clearInterval(timer);
    }, 500);
  }

  const stage: LesenStage = {
    el: root,
    onStatus(fresh) {
      list = fresh;
      const node = renderPagesList(fresh, view.pages);
      pages.replaceWith(node);
      pages = node;
      advance();
    },
    onItem(item) {
      const body = details.body;
      const pinned = body.scrollTop > 0 && body.scrollHeight - body.scrollTop - body.clientHeight < 48;
      const li = streamItemNode(item, false);
      stream.append(li);
      requestAnimationFrame(() => li.classList.add('in'));
      // An open popup follows the newest line once the visitor has scrolled to the end.
      if (details.el.open && pinned) body.scrollTop = body.scrollHeight;
      n += 1;
      if (item.source === 'page') observations += 1;
      count.textContent = String(n);
      total.textContent = String(n);
      advance();
    },
    onDone() {
      finish('is-done', t('lesen.counter_done'));
      paint(100);
      countLabel.textContent = t('lesen.counter_done');
      next.disabled = false;
      fit();
      startAuto();
    },
    onError(message) {
      finish('is-bad', t('lesen.progress.failed'));
      errorBox.textContent = message;
      errorBox.hidden = false;
      next.disabled = true;
      fit();
    },
    description: () => (editing ? textarea.value.trim() : saved),
    draft: box
  };
  if (view.status !== 'lesen') stage.onDone();
  return stage;
}
